import { z } from 'zod';

export type MarketplaceRole = 'platform_owner' | 'curator' | 'partner_operator';
export type OpportunityAccessTier = 'public' | 'verified_partner' | 'owner_signature';
export type OpportunityOperatingLane = 'partner_seller_of_record' | 'owner_direct';
export type OpportunityEvidenceState = 'current' | 'unknown' | 'expired' | 'rejected';
export type OpportunityReadiness = 'planning_only' | 'partner_ready' | 'owner_only' | 'paused';

export const startupCapitalInputSchema = z.object({
  salePriceKrw: z.number().int().min(1).max(100_000_000),
  productCostKrw: z.number().int().min(0).max(100_000_000),
  sourceShippingKrw: z.number().int().min(0).max(100_000_000),
  taxAndDutyReserveKrw: z.number().int().min(0).max(100_000_000),
  outboundShippingKrw: z.number().int().min(0).max(100_000_000),
  packagingKrw: z.number().int().min(0).max(100_000_000),
  channelFeeRateBps: z.number().int().min(0).max(9_000),
  paymentFeeRateBps: z.number().int().min(0).max(9_000),
  returnReserveRateBps: z.number().int().min(0).max(9_000),
  sampleUnits: z.number().int().min(0).max(100),
  listingPreparationKrw: z.number().int().min(0).max(100_000_000),
  channelSetupKrw: z.number().int().min(0).max(100_000_000),
  bufferOrders: z.number().int().min(1).max(1_000)
});

export type StartupCapitalInput = z.infer<typeof startupCapitalInputSchema>;
export type StartupCapitalResult = {
  minimumCashKrw: number;
  sampleAndSetupKrw: number;
  workingCapitalKrw: number;
  returnReserveKrw: number;
  contributionKrw: number;
  contributionRate: number;
  breakEvenOrders: number | null;
  ready: boolean;
  blockers: string[];
};

export function calculateStartupCapital(input: StartupCapitalInput): StartupCapitalResult {
  const feeKrw = Math.ceil(input.salePriceKrw * (input.channelFeeRateBps + input.paymentFeeRateBps) / 10_000);
  const returnReservePerOrderKrw = Math.ceil(input.salePriceKrw * input.returnReserveRateBps / 10_000);
  const cashCostPerOrderKrw = input.productCostKrw + input.sourceShippingKrw + input.taxAndDutyReserveKrw + input.outboundShippingKrw + input.packagingKrw;
  const contributionKrw = input.salePriceKrw - cashCostPerOrderKrw - feeKrw - returnReservePerOrderKrw;
  const contributionRate = contributionKrw / input.salePriceKrw;
  const sampleAndSetupKrw = (input.productCostKrw + input.sourceShippingKrw + input.taxAndDutyReserveKrw) * input.sampleUnits
    + input.listingPreparationKrw
    + input.channelSetupKrw;
  const workingCapitalKrw = cashCostPerOrderKrw * input.bufferOrders;
  const returnReserveKrw = returnReservePerOrderKrw * input.bufferOrders;
  const minimumCashKrw = sampleAndSetupKrw + workingCapitalKrw + returnReserveKrw;
  const blockers: string[] = [];
  if (contributionKrw < 5_000) blockers.push('economics:contribution_below_5000_krw');
  if (contributionRate < 0.25) blockers.push('economics:contribution_rate_below_25_percent');
  const breakEvenOrders = contributionKrw > 0 ? Math.ceil(sampleAndSetupKrw / contributionKrw) : null;
  return { minimumCashKrw, sampleAndSetupKrw, workingCapitalKrw, returnReserveKrw, contributionKrw, contributionRate, breakEvenOrders, ready: blockers.length === 0, blockers };
}

const evidenceSchema = z.object({
  supplyAndResaleRights: z.enum(['current', 'unknown', 'expired', 'rejected']),
  imageAndCopyRights: z.enum(['current', 'unknown', 'expired', 'rejected']),
  productSafetyAndLabeling: z.enum(['current', 'unknown', 'expired', 'rejected']),
  channelEligibility: z.enum(['current', 'unknown', 'expired', 'rejected']),
  returnsRoute: z.enum(['current', 'unknown', 'expired', 'rejected']),
  stockAndPriceFeed: z.enum(['current', 'unknown', 'expired', 'rejected']),
  costQuote: z.enum(['current', 'unknown', 'expired', 'rejected'])
});

export const partnerOpportunitySchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().regex(/^CA-OPP-[A-Z0-9-]{3,40}$/),
  title: z.string().min(2).max(80),
  category: z.string().min(2).max(60),
  summary: z.string().min(8).max(240),
  originCountry: z.enum(['KR', 'JP', 'CN']),
  accessTier: z.enum(['public', 'verified_partner', 'owner_signature']),
  operatingLane: z.enum(['partner_seller_of_record', 'owner_direct']),
  qualityLabel: z.string().min(2).max(40),
  customerFit: z.array(z.string().min(2).max(80)).min(1).max(6),
  supportedChannels: z.array(z.enum(['owned_web', 'naver', 'coupang', 'buyma'])).min(1).max(4),
  economics: startupCapitalInputSchema,
  evidence: evidenceSchema,
  curationNote: z.string().min(8).max(300),
  status: z.enum(['draft', 'open', 'paused']),
  updatedAt: z.string().datetime()
});

export type PartnerOpportunity = z.infer<typeof partnerOpportunitySchema>;

export const ownerOpportunityInputSchema = partnerOpportunitySchema.omit({ schemaVersion: true, updatedAt: true }).extend({
  idempotencyKey: z.string().startsWith('sha256:').min(16).max(160)
});

export const partnerSelectionInputSchema = z.object({
  opportunityId: z.string().regex(/^CA-OPP-[A-Z0-9-]{3,40}$/),
  economics: startupCapitalInputSchema,
  idempotencyKey: z.string().startsWith('sha256:').min(16).max(160)
});

export type PartnerSelectionRecord = {
  id: string;
  partnerId: string;
  opportunityId: string;
  idempotencyKey: string;
  state: 'watching' | 'planned';
  economicsInput: StartupCapitalInput;
  economicsResult: StartupCapitalResult;
  externalExecution: 'none';
  createdAt: string;
  updatedAt: string;
};

const evidenceLabels: Record<keyof PartnerOpportunity['evidence'], string> = {
  supplyAndResaleRights: '공급·재판매 권리',
  imageAndCopyRights: '이미지·상세정보 사용권',
  productSafetyAndLabeling: '안전·표시 검토',
  channelEligibility: '판매채널 자격',
  returnsRoute: '반품 경로',
  stockAndPriceFeed: '재고·가격 갱신',
  costQuote: '현재 원가 견적'
};

export function evaluatePartnerOpportunity(opportunity: PartnerOpportunity): { readiness: OpportunityReadiness; blockers: string[]; economics: StartupCapitalResult } {
  const economics = calculateStartupCapital(opportunity.economics);
  const blockers = Object.entries(opportunity.evidence)
    .filter(([, state]) => state !== 'current')
    .map(([field, state]) => `${evidenceLabels[field as keyof PartnerOpportunity['evidence']]} ${state === 'unknown' ? '확인 전' : state === 'expired' ? '갱신 필요' : '기각됨'}`);
  blockers.push(...economics.blockers);
  if (opportunity.status !== 'open') blockers.push(opportunity.status === 'paused' ? '오너가 배포를 일시 정지함' : '오너 검토 중');
  if (opportunity.accessTier === 'owner_signature' || opportunity.operatingLane === 'owner_direct') return { readiness: opportunity.status === 'paused' ? 'paused' : 'owner_only', blockers: [...new Set(blockers)], economics };
  return { readiness: blockers.length === 0 ? 'partner_ready' : opportunity.status === 'paused' ? 'paused' : 'planning_only', blockers: [...new Set(blockers)], economics };
}

export function canAccessOpportunity(role: MarketplaceRole, accessTier: OpportunityAccessTier, opportunity: PartnerOpportunity): boolean {
  if (role === 'platform_owner' || role === 'curator') return true;
  if (opportunity.accessTier === 'owner_signature' || opportunity.operatingLane === 'owner_direct') return false;
  return opportunity.accessTier === 'public' || accessTier === 'verified_partner';
}

const unknownEvidence: PartnerOpportunity['evidence'] = {
  supplyAndResaleRights: 'unknown', imageAndCopyRights: 'unknown', productSafetyAndLabeling: 'unknown', channelEligibility: 'unknown', returnsRoute: 'unknown', stockAndPriceFeed: 'unknown', costQuote: 'unknown'
};

const baseEconomics: StartupCapitalInput = {
  salePriceKrw: 29_000, productCostKrw: 10_000, sourceShippingKrw: 1_000, taxAndDutyReserveKrw: 0, outboundShippingKrw: 3_200, packagingKrw: 600,
  channelFeeRateBps: 600, paymentFeeRateBps: 330, returnReserveRateBps: 500, sampleUnits: 1, listingPreparationKrw: 30_000, channelSetupKrw: 0, bufferOrders: 3
};

/** 실제 판매 승인이 아니라 저자본 계획을 시작하기 위한 카테고리 단위 fixture다. */
export const initialPartnerOpportunities: PartnerOpportunity[] = [
  {
    schemaVersion: 1, id: 'CA-OPP-KR-STATIONERY', title: '한국 문구·책상 소품', category: '문구·데스크', summary: '작고 비전기인 생활 문구를 한 개 샘플부터 검증하는 공개 계획 후보입니다.', originCountry: 'KR', accessTier: 'public', operatingLane: 'partner_seller_of_record', qualityLabel: '공개 탐색', customerFit: ['정리와 기록을 즐기는 고객', '선물 가능한 소형 생활도구'], supportedChannels: ['owned_web', 'naver', 'coupang'], economics: baseEconomics, evidence: unknownEvidence, curationNote: '공개 자료만으로 공급·권리·마진을 확정하지 않습니다. 계산 후 현재 증거를 하나씩 닫습니다.', status: 'open', updatedAt: '2026-08-26T00:00:00.000Z'
  },
  {
    schemaVersion: 1, id: 'CA-OPP-KR-CRAFT', title: '한국 공예·인테리어 소품', category: '공예·홈데코', summary: '제작자와 직접 계약해 이미지·일본 판매 범위를 확보한 뒤 파트너에게 여는 검증 후보입니다.', originCountry: 'KR', accessTier: 'verified_partner', operatingLane: 'partner_seller_of_record', qualityLabel: '파트너 검증 후보', customerFit: ['작은 브랜드의 제작 배경을 찾는 고객', '한국적이되 일상에서 쓰는 소품'], supportedChannels: ['owned_web', 'naver', 'buyma'], economics: { ...baseEconomics, salePriceKrw: 49_000, productCostKrw: 18_000, listingPreparationKrw: 50_000 }, evidence: unknownEvidence, curationNote: '제작자 수권·정품·표시·현지 반품비가 닫힌 뒤에만 판매 실행 자료를 엽니다.', status: 'open', updatedAt: '2026-08-26T00:00:00.000Z'
  },
  {
    schemaVersion: 1, id: 'CA-OPP-OWNER-SIGNATURE', title: '반짝서랍 직영·독점 브랜드', category: '직영 셀렉션', summary: '오너가 직접 계약하고 품질·콘텐츠·재고를 책임지는 직영 상품군입니다.', originCountry: 'KR', accessTier: 'owner_signature', operatingLane: 'owner_direct', qualityLabel: '반짝서랍 직영', customerFit: ['직영 검수와 단독 구성을 원하는 고객'], supportedChannels: ['owned_web', 'naver', 'coupang', 'buyma'], economics: { ...baseEconomics, salePriceKrw: 59_000, productCostKrw: 20_000, listingPreparationKrw: 100_000 }, evidence: unknownEvidence, curationNote: '파트너 목록에서 숨기고 고객에게는 직영·독점 근거를 명확히 표시합니다.', status: 'open', updatedAt: '2026-08-26T00:00:00.000Z'
  }
];

export const partnerMarketplacePolicy = {
  checkedAt: '2026-08-26',
  model: 'b2b_opportunity_toolkit' as const,
  marketplacePayments: 'not_implemented' as const,
  platformSellerOfRecordForPartnerOrders: false,
  partnerKeepsOwnSellerAccount: true,
  credentialSharing: 'forbidden' as const,
  customerPiiSharing: 'forbidden' as const,
  ownerSignatureDisclosure: 'required' as const,
  externalExecution: 'none' as const
};
